// src/cockpit4/components/SmartPricingAssistant.tsx
// ============================================
// 智能定价助手（财务中心 P2-功能9）- v2.0 详细改进计划
// 成本+市场+竞争定价 / 价格弹性分析 / 定价策略模拟 / 利润最大化建议
// 数据来源：localStorage（xeaos_smart_pricing）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Tag, TrendingUp, FlaskConical, Calculator } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

interface ProductPricing {
  id: string;
  name: string;
  cost: number;
  currentPrice: number;
  suggestedPrice: number;
  margin: number;
  elasticity: number;
  units: number;
}

const INITIAL: ProductPricing[] = [
  { id: "pp1", name: "XEAOS 专业版", cost: 320, currentPrice: 598, suggestedPrice: 680, margin: 46.5, elasticity: -1.4, units: 45 },
  { id: "pp2", name: "AI 营销包", cost: 180, currentPrice: 399, suggestedPrice: 450, margin: 54.9, elasticity: -1.1, units: 62 },
  { id: "pp3", name: "量化系统", cost: 560, currentPrice: 998, suggestedPrice: 1080, margin: 43.9, elasticity: -0.8, units: 18 },
  { id: "pp4", name: "咨询服务", cost: 1500, currentPrice: 8000, suggestedPrice: 8500, margin: 81.3, elasticity: -0.5, units: 6 },
];

const KEY = "xeaos_smart_pricing";

function load(): ProductPricing[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as ProductPricing[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function SmartPricingAssistant() {
  const [products, setProducts] = useState<ProductPricing[]>(load);
  const [selected, setSelected] = useState<string>("pp1");

  const active = products.find((p) => p.id === selected) ?? products[0];

  // 价格弹性模拟：在不同价格点预测销量与利润
  const simulation = useMemo(() => {
    const pts: { price: number; units: number; profit: number }[] = [];
    const base = active;
    const steps = [-20, -10, 0, 10, 20];
    for (const delta of steps) {
      const price = Math.round(base.currentPrice * (1 + delta / 100));
      const elasticityFactor = 1 + (base.elasticity * delta) / 100;
      const units = Math.max(0, Math.round(base.units * elasticityFactor));
      const profit = (price - base.cost) * units;
      pts.push({ price, units, profit });
    }
    return pts;
  }, [active]);

  const maxProfit = useMemo(() => simulation.reduce((a, p) => Math.max(a, p.profit), 0), [simulation]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag size={14} className="text-purple-400" />
        <h3 className="text-[12px] font-medium text-white">智能定价助手</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Smart Pricing</span>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Calculator size={11} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">产品定价 · 点击模拟</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {products.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              className={`rounded-lg border p-2.5 text-left transition-colors ${p.id === selected ? "border-purple-500/40 bg-purple-500/10" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"}`}
            >
              <div className="text-[10px] font-medium text-white">{p.name}</div>
              <div className="text-[9px] font-mono text-gray-500 mt-0.5">成本 ¥{p.cost}</div>
              <div className="text-[11px] font-mono font-semibold text-purple-300 mt-1">¥{p.currentPrice}</div>
              <div className="text-[9px] font-mono text-emerald-400 mt-0.5">建议 ¥{p.suggestedPrice}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{active.name} · 价格弹性模拟</span>
          </div>
          <div className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={simulation} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="price" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} label={{ value: "价格 (¥)", fill: "#64748B", fontSize: 9, fontFamily: "monospace", position: "insideBottom", offset: -2 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={48} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} formatter={(v: any, n: any) => [n === "利润" ? `¥${Number(v).toLocaleString()}` : `${v} 份`, n]} />
                <Line type="monotone" dataKey="profit" name="利润" stroke="#34D399" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="units" name="销量" stroke="#38BDF8" strokeWidth={1.5} dot={{ r: 3 }} />
                <ReferenceLine x={active.suggestedPrice} stroke="#A78BFA" strokeDasharray="4 3" label={{ value: "建议价", fill: "#A78BFA", fontSize: 9, fontFamily: "monospace" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[9px] font-mono text-emerald-400 mt-1">当前利润 ¥{simulation[2]?.profit.toLocaleString()} → 最优利润 ¥{maxProfit.toLocaleString()}（提升 {maxProfit > 0 && simulation[2]?.profit ? Math.round(((maxProfit - simulation[2].profit) / Math.max(1, simulation[2].profit)) * 100) : 0}%）</div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">定价策略建议</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            {[
              { color: "#34D399", text: `建议价 ¥${active.suggestedPrice}（+${Math.round(((active.suggestedPrice - active.currentPrice) / active.currentPrice) * 100)}%），弹性 -${Math.abs(active.elasticity)}，提价空间充足` },
              { color: "#FBBF24", text: "价格弹性低（|e| < 1.5）→ 适合小幅提价测试，风险可控" },
              { color: "#38BDF8", text: "A/B 测试建议：专业版按 ¥598 vs ¥680 分两组投放测试" },
              { color: "#F87171", text: "若提价 20% 后销量下降 >15%，建议回落至当前价并优化价值包装" },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
                <span className="text-gray-400 leading-relaxed">{s.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

